/**
 * Project API Client - Esempi di utilizzo delle API Project
 * 
 * Questo file mostra come utilizzare le API del progetto dal frontend
 */

const API_BASE_URL = '/api/v1/project/project';

class ProjectAPI {
    
    /**
     * Ottiene la lista dei progetti con lo schema DBMng
     * @returns {Promise} Promise che risolve con {ok, data, dbmng}
     */
    async getProjects() {
        try {
            const response = await fetch(API_BASE_URL);
            const result = await response.json();
            
            if (result.ok) {
                console.log('Progetti caricati:', result.data.length);
                console.log('Schema DBMng:', result.dbmng);
            } else {
                console.error('Errore API:', result.message);
            }
            
            return result;
        } catch (error) {
            console.error('Errore nel caricamento progetti:', error);
            return { ok: false, message: error.message };
        }
    }
    
    /**
     * Ottiene un singolo progetto per ID
     * @param {number} projectId ID del progetto
     * @returns {Promise} Promise che risolve con {ok, data} o {ok, message}
     */
    async getProject(projectId) {
        try {
            const response = await fetch(`${API_BASE_URL}/${projectId}`);
            const result = await response.json();
            
            
            if (!result.ok) {
                console.error('Errore API:', result.message);
            }
            
            return result;
        } catch (error) {
            console.error('Errore nel caricamento progetto:', error);
            return { ok: false, message: error.message };
        }
    }
    
    /**
     * Crea un nuovo progetto
     * @param {Object} projectData Dati del progetto {project_name, budget}
     * @returns {Promise} Promise che risolve con {ok, data} o {ok, message}
     */
    async createProject(projectData) {
        try {
            const response = await fetch(API_BASE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(projectData)
            });
            
            const result = await response.json();
            
            if (result.ok) {
                console.log('Progetto creato:', result.data);
            } else {
                console.error('Errore API:', result.message);
            }
            
            return result;
        } catch (error) {
            console.error('Errore nella creazione progetto:', error);
            return { ok: false, message: error.message };
        }
    }
    
    /**
     * Aggiorna un progetto esistente
     * @param {number} projectId ID del progetto
     * @param {Object} projectData Dati aggiornati del progetto
     * @returns {Promise} Promise che risolve con {ok, data} o {ok, message}
     */
    async updateProject(projectId, projectData) {
        try {
            const response = await fetch(`${API_BASE_URL}/${projectId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(projectData)
            });
            
            const result = await response.json();
            
            if (result.ok) {
                console.log('Progetto aggiornato:', result.data);
            } else {
                console.error('Errore API:', result.message);
            }
            
            return result;
        } catch (error) {
            console.error('Errore nell\'aggiornamento progetto:', error);
            return { ok: false, message: error.message };
        }
    }
    
    /**
     * Elimina un progetto (soft delete)
     * @param {number} projectId ID del progetto
     * @returns {Promise} Promise che risolve con {ok} o {ok, message}
     */
    async deleteProject(projectId) {
        try {
            const response = await fetch(`${API_BASE_URL}/${projectId}`, {
                method: 'DELETE'
            });
            
            const result = await response.json();
            
            if (result.ok) {
                console.log('Progetto eliminato');
            } else {
                console.error('Errore API:', result.message);
            }
            
            return result;
        } catch (error) {
            console.error('Errore nell\'eliminazione progetto:', error);
            return { ok: false, message: error.message };
        }
    }
}

/**
 * Esempio di utilizzo delle API Project
 */
async function exempleUsage() {
    const projectAPI = new ProjectAPI();
    
    try {
        // 1. Carica progetti esistenti
        console.log('=== Caricamento progetti ===');
        const projectsResult = await projectAPI.getProjects();
        
        if (projectsResult.ok) {
            const projects = projectsResult.data;
            const schema = projectsResult.dbmng;
            
            console.log(`Trovati ${projects.length} progetti`);
            
            // Genera form dinamico dal schema DBMng
            generateProjectForm(schema);
            
            // Popola tabella con i dati
            populateProjectsTable(projects);
        }
        
        // 2. Crea nuovo progetto
        console.log('\n=== Creazione nuovo progetto ===');
        const newProjectData = {
            project_name: 'Nuovo Progetto Test',
            budget: 25000.50
        };
        
        const createResult = await projectAPI.createProject(newProjectData);
        
        if (createResult.ok) {
            const newProjectId = createResult.data.id;
            console.log(`Progetto creato con ID: ${newProjectId}`);
            
            // 3. Aggiorna il progetto appena creato
            console.log('\n=== Aggiornamento progetto ===');
            const updateData = {
                project_name: 'Progetto Test - Aggiornato',
                budget: 30000.00
            };
            
            await projectAPI.updateProject(newProjectId, updateData);
            
            // 4. Ricarica singolo progetto per vedere le modifiche
            const updatedProject = await projectAPI.getProject(newProjectId);
            console.log('Progetto aggiornato:', updatedProject.data);
            
            // 5. (Opzionale) Elimina progetto di test
            // await projectAPI.deleteProject(newProjectId);
        }
        
    } catch (error) {
        console.error('Errore negli esempi:', error);
    }
}

/**
 * Genera form dinamico dal schema DBMng
 * @param {Object} schema Schema DBMng
 */
function generateProjectForm(schema) {
    console.log('Generazione form dal schema DBMng...');
    
    const fields = schema.fields;
    let formHTML = '<form id="projectForm">';
    
    for (const [fieldName, fieldConfig] of Object.entries(fields)) {
        // Skip campi readonly
        if (fieldConfig.readonly) continue;
        
        formHTML += `
            <div class="form-group">
                <label for="${fieldName}">${fieldConfig.label}</label>
        `;
        
        if (fieldConfig.widget === 'textarea') {
            formHTML += `<textarea id="${fieldName}" name="${fieldName}" class="form-control"`;
        } else {
            const inputType = fieldConfig.type === 'float' ? 'number' : 'text';
            formHTML += `<input type="${inputType}" id="${fieldName}" name="${fieldName}" class="form-control"`;
            
            if (fieldConfig.type === 'float') {
                formHTML += ' step="0.01"';
            }
        }
        
        if (fieldConfig.nullable === 0) {
            formHTML += ' required';
        }
        
        if (fieldConfig.max_length) {
            formHTML += ` maxlength="${fieldConfig.max_length}"`;
        }
        
        formHTML += fieldConfig.widget === 'textarea' ? '></textarea>' : '>';
        formHTML += '</div>';
    }
    
    formHTML += `
        <button type="submit" class="btn btn-primary">Salva Progetto</button>
        </form>
    `;
    
    // In un'app reale, inseriresti questo HTML nel DOM
    console.log('Form HTML generato:', formHTML);
}

/**
 * Popola tabella progetti
 * @param {Array} projects Array di progetti
 */
function populateProjectsTable(projects) {
    console.log('Popolazione tabella progetti...');
    
    let tableHTML = `
        <table class="table">
            <thead>
                <tr>
                    <th>ID</th>
                    <th>Nome Progetto</th>
                    <th>Budget</th>
                    <th>Data Creazione</th>
                    <th>Azioni</th>
                </tr>
            </thead>
            <tbody>
    `;
    
    projects.forEach(project => {
        tableHTML += `
            <tr>
                <td>${project.id}</td>
                <td>${project.project_name}</td>
                <td>€ ${parseFloat(project.budget).toLocaleString('it-IT', {minimumFractionDigits: 2})}</td>
                <td>${new Date(project.created_at).toLocaleDateString('it-IT')}</td>
                <td>
                    <button onclick="editProject(${project.id})" class="btn btn-sm btn-primary">Modifica</button>
                    <button onclick="deleteProject(${project.id})" class="btn btn-sm btn-danger">Elimina</button>
                </td>
            </tr>
        `;
    });
    
    tableHTML += '</tbody></table>';
    
    // In un'app reale, inseriresti questo HTML nel DOM
    console.log('Tabella HTML generata');
}

// Esporta la classe per l'uso in altri moduli
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ProjectAPI;
}

// Export ES6 per Vue.js
export default ProjectAPI;

// Esempio di chiamata (decommentare per testare)
// exempleUsage();